import { Box, Chip, Divider, Tab, Tabs, Typography } from "@mui/material";
import { useEffect, useMemo, useState, type ReactElement } from "react";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import ShowChartOutlinedIcon from "@mui/icons-material/ShowChartOutlined";
import GradientOutlinedIcon from "@mui/icons-material/GradientOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import RadarOutlinedIcon from "@mui/icons-material/RadarOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import DeviceHubOutlinedIcon from "@mui/icons-material/DeviceHubOutlined";
import { useTranslation } from "react-i18next";
import {
  applyOptionFieldsChange,
  type WidgetChartMapping,
} from "../../utils/chartMappingBuilder";
import {
  groupOptionFieldsByPanel,
  type ChartOptionFieldDef,
  type ChartOptionPanelId,
} from "../../utils/chartOptionRegistry";
import { getOptionPath } from "../../utils/chartOptionPaths";
import { AppFormGrid } from "../layout";
import { chartOptionFormGridSx, chartOptionSectionLabelSx } from "../../lib/ui/formLayout";
import ChartGridPaddingControl from "./ChartGridPaddingControl";
import ChartOptionCompactField from "./ChartOptionCompactField";
import TitleLegendPanel from "./TitleLegendPanel";
import { filterRenderableFields, groupFieldsForPanel, type GridPaddingValues } from "./chartOptionLayout";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

const CHART_PANEL_ICONS: Record<ChartOptionPanelId, ReactElement> = {
  general: <TuneOutlinedIcon fontSize="small" />,
  titleLegend: <TitleOutlinedIcon fontSize="small" />,
  tooltip: <ChatBubbleOutlineOutlinedIcon fontSize="small" />,
  grid: <GridOnOutlinedIcon fontSize="small" />,
  axes: <TimelineOutlinedIcon fontSize="small" />,
  series: <ShowChartOutlinedIcon fontSize="small" />,
  visualMap: <GradientOutlinedIcon fontSize="small" />,
  calendar: <CalendarMonthOutlinedIcon fontSize="small" />,
  radar: <RadarOutlinedIcon fontSize="small" />,
  gauge: <SpeedOutlinedIcon fontSize="small" />,
  hierarchical: <AccountTreeOutlinedIcon fontSize="small" />,
  flow: <DeviceHubOutlinedIcon fontSize="small" />,
};

type Props = {
  mapping: WidgetChartMapping;
  onChange: (next: WidgetChartMapping) => void;
  disabled?: boolean;
};

function CompactSection({
  labelKey,
  fields,
  mapping,
  disabled,
  onChange,
}: {
  labelKey?: string;
  fields: ChartOptionFieldDef[];
  mapping: WidgetChartMapping;
  disabled?: boolean;
  onChange: (next: WidgetChartMapping) => void;
}) {
  const { t } = useTranslation();
  if (!fields.length) return null;

  return (
    <Box>
      {labelKey ? (
        <Typography variant="overline" sx={chartOptionSectionLabelSx}>
          {t(labelKey)}
        </Typography>
      ) : null}
      <AppFormGrid spacing="compact" alignItems="stretch" sx={chartOptionFormGridSx}>
        {fields.map((field) => (
          <ChartOptionCompactField
            key={field.id}
            field={field}
            mapping={mapping}
            disabled={disabled}
            onChange={onChange}
          />
        ))}
      </AppFormGrid>
    </Box>
  );
}

function ChartOptionPanelBody({
  panel,
  fields,
  mapping,
  disabled,
  onChange,
}: {
  panel: ChartOptionPanelId;
  fields: ChartOptionFieldDef[];
  mapping: WidgetChartMapping;
  disabled?: boolean;
  onChange: (next: WidgetChartMapping) => void;
}) {
  const custom = useAppCustomStyles();
  const groups = useMemo(() => groupFieldsForPanel(panel, fields), [panel, fields]);

  const applyGridPadding = (next: GridPaddingValues) => {
    onChange(
      applyOptionFieldsChange(mapping, [
        { path: "grid.left", value: next.left },
        { path: "grid.right", value: next.right },
        { path: "grid.top", value: next.top },
        { path: "grid.bottom", value: next.bottom },
      ])
    );
  };

  if (panel === "titleLegend") {
    return <TitleLegendPanel fields={fields} mapping={mapping} disabled={disabled} onChange={onChange} />;
  }

  if (panel === "grid") {
    const extraFields = filterRenderableFields(fields);
    return (
      <Box>
        <ChartGridPaddingControl
          values={{
            left: String(getOptionPath(mapping.optionTemplate, "grid.left") ?? ""),
            right: String(getOptionPath(mapping.optionTemplate, "grid.right") ?? ""),
            top: String(getOptionPath(mapping.optionTemplate, "grid.top") ?? ""),
            bottom: String(getOptionPath(mapping.optionTemplate, "grid.bottom") ?? ""),
          }}
          disabled={disabled}
          onChange={applyGridPadding}
        />
        {extraFields.length > 0 && (
          <>
            <Divider sx={custom.components.chartOptionPanels.divider} />
            <AppFormGrid spacing="compact" alignItems="stretch" sx={chartOptionFormGridSx}>
              {extraFields.map((field) => (
                <ChartOptionCompactField
                  key={field.id}
                  field={field}
                  mapping={mapping}
                  disabled={disabled}
                  onChange={onChange}
                />
              ))}
            </AppFormGrid>
          </>
        )}
      </Box>
    );
  }

  if (groups.length <= 1) {
    return (
      <AppFormGrid spacing="compact" alignItems="stretch" sx={chartOptionFormGridSx}>
        {groups[0]?.fields.map((field) => (
          <ChartOptionCompactField
            key={field.id}
            field={field}
            mapping={mapping}
            disabled={disabled}
            onChange={onChange}
          />
        ))}
      </AppFormGrid>
    );
  }

  return (
    <Box>
      {groups.map((group, index) => (
        <Box key={group.id}>
          <CompactSection
            labelKey={group.labelKey}
            fields={group.fields}
            mapping={mapping}
            disabled={disabled}
            onChange={onChange}
          />
          {index < groups.length - 1 ? <Divider sx={custom.components.chartOptionPanels.divider} /> : null}
        </Box>
      ))}
    </Box>
  );
}

export default function ChartOptionPanels({ mapping, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const panelStyles = custom.components.chartOptionPanels;
  const groups = groupOptionFieldsByPanel(mapping.chartType);
  const [activePanel, setActivePanel] = useState<ChartOptionPanelId>(groups[0]?.panel ?? "general");

  useEffect(() => {
    if (!groups.some((group) => group.panel === activePanel)) {
      setActivePanel(groups[0]?.panel ?? "general");
    }
  }, [groups, activePanel]);

  const activeGroup = groups.find((group) => group.panel === activePanel);

  if (!groups.length) return null;

  return (
    <Box sx={panelStyles.root}>
      <Box sx={panelStyles.headerBlock}>
        <Typography variant="subtitle2" fontWeight={700} sx={panelStyles.title}>
          {t("chartOptions.title")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={panelStyles.subtitle}>
          {t("chartOptions.subtitle")}
        </Typography>
      </Box>

      <Tabs
        value={activePanel}
        onChange={(_, value: ChartOptionPanelId) => setActivePanel(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={panelStyles.tabs}
      >
        {groups.map(({ panel, fields }) => (
          <Tab
            key={panel}
            value={panel}
            icon={CHART_PANEL_ICONS[panel]}
            iconPosition="start"
            label={
              <Box sx={panelStyles.tabLabelBox}>
                <Typography component="span" variant="inherit">
                  {t(`chartOptions.panels.${panel}`)}
                </Typography>
                <Chip
                  size="small"
                  label={fields.length}
                  sx={panelStyles.tabChip}
                />
              </Box>
            }
          />
        ))}
      </Tabs>

      <Box sx={panelStyles.panelBody}>
        {activeGroup && (
          <ChartOptionPanelBody
            panel={activeGroup.panel}
            fields={activeGroup.fields}
            mapping={mapping}
            disabled={disabled}
            onChange={onChange}
          />
        )}
      </Box>
    </Box>
  );
}
